import React from 'react';
import SEO from '../components/SEO';
import './Download.css';

/**
 * Download Page Component
 * Dedicated page for downloading lockitup software
 * Includes why users need it, features, how to use, and download button
 */
function Download() {
  return (
    <div className="page-container download-page">
      {/* SEO Meta Tags */}
      <SEO 
        title="Download lockitup – Free Laptop Security Software"
        description="Download lockitup.exe for free. Secure your laptop before repair with password-protected fullscreen lock. Works on Windows 10 & 11. No installation needed."
        keywords="download lockitup, free laptop lock, download security software, laptop repair protection, Windows security tool"
        ogTitle="Download lockitup – Free Laptop Security Software"
        ogDescription="Download lockitup.exe for free. Secure your laptop before giving it for repair."
        ogUrl="https://www.lockitup.tech/download"
      />

      {/* Hero Section */}
      <section className="download-hero">
        <div className="container">
          <div className="download-hero-content">
            {/* Lock Icon */}
            <div className="download-icon">
              <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="25" y="45" width="50" height="40" rx="4" fill="#27AE60" stroke="#F1C40F" strokeWidth="3"/>
                <path d="M 35 45 L 35 32 Q 35 20, 50 20 Q 65 20, 65 32 L 65 45" stroke="#F1C40F" strokeWidth="5" fill="none" strokeLinecap="round"/>
                <circle cx="50" cy="65" r="5" fill="#1A1F36"/>
                <rect x="48" y="65" width="4" height="10" fill="#1A1F36"/>
              </svg>
            </div>

            <h1 className="download-title">Secure your laptop before giving it for repair</h1>
            
            <div className="download-intro">
              <p>Your laptop contains personal photos, documents, passwords, and private data.</p>
              <p>When you hand it over for repair, anyone could access your files without your knowledge.</p>
              <p className="highlight"><strong>lockitup solves this problem instantly.</strong></p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Solution Section */}
      <section className="solution-section">
        <div className="container">
          <div className="solution-content">
            <p className="solution-text">
              It creates a secure fullscreen lock on your laptop that no technician or stranger can bypass.
            </p>
            <p className="solution-text highlight">
              <strong>Only you can unlock it with your password.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Why Trust Section */}
      <section className="trust-section">
        <div className="container">
          <h2 className="section-heading">✅ Why users trust lockitup</h2>
          
          <div className="trust-grid">
            <div className="trust-item">
              <div className="trust-icon">💯</div>
              <h3>100% Free</h3>
              <p>No hidden charges</p>
            </div>

            <div className="trust-item">
              <div className="trust-icon">🔌</div>
              <h3>Works completely offline</h3>
              <p>No internet needed</p>
            </div>

            <div className="trust-item">
              <div className="trust-icon">🔒</div>
              <h3>No data collection</h3>
              <p>Your files stay private</p>
            </div>

            <div className="trust-item">
              <div className="trust-icon">⚡</div>
              <h3>No installation required</h3>
              <p>Just open and lock</p>
            </div>

            <div className="trust-item">
              <div className="trust-icon">🪶</div>
              <h3>Lightweight and fast</h3>
              <p>Minimal resource usage</p>
            </div>

            <div className="trust-item">
              <div className="trust-icon">👥</div>
              <h3>Built for everyone</h3>
              <p>Not just tech experts</p>
            </div>
          </div>

          <p className="trust-tagline">Your privacy stays in your hands.</p>
        </div>
      </section>

      {/* Download CTA Section */}
      <section className="download-cta-section">
        <div className="container">
          <div className="download-cta-box">
            <h2 className="cta-title">⬇ Download Now</h2>
            
            <a 
              href="https://github.com/lockitup/lockitup/releases/latest/download/lockitup.exe" 
              className="download-cta-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 3V16M12 16L8 12M12 16L16 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 17V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V17" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Download lockitup.exe
            </a>

            <div className="download-details">
              <p>✔ Works on Windows 10 & 11</p>
              <p>✔ No installation needed</p>
              <p>✔ Free forever</p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="how-to-use-section">
        <div className="container">
          <h2 className="section-heading">⚙ How to Use</h2>
          
          <div className="steps-list">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Download lockitup.exe</h3>
                <p>Click the download button above to get the file</p>
              </div>
            </div>

            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Open the file and set your password</h3>
                <p>Double-click the .exe file and create a secure password</p>
              </div>
            </div>

            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Click "Lock" — your laptop is now protected</h3>
                <p>Your screen will be locked and fully secure</p>
              </div>
            </div>
          </div>

          <p className="unlock-note">
            When you return from repair, simply enter your password to unlock.
          </p>
        </div>
      </section>

      {/* Forgot Password Section */}
      <section className="forgot-password-section">
        <div className="container">
          <div className="forgot-password-box">
            <h2 className="forgot-password-title">🔓 Forgot Password? How to Unlock</h2>
            
            <p className="forgot-password-intro">
              lockitup cannot recover passwords to keep your data fully secure.
              <br />
              If you forget your password, follow these simple steps:
            </p>

            <div className="recovery-steps">
              <div className="recovery-step">
                <div className="recovery-step-number">1</div>
                <div className="recovery-step-content">
                  <h3>Restart your laptop</h3>
                </div>
              </div>

              <div className="recovery-step">
                <div className="recovery-step-number">2</div>
                <div className="recovery-step-content">
                  <h3>Enter Safe Mode</h3>
                  <p>Hold Shift and click Restart, then select Safe Mode.</p>
                </div>
              </div>

              <div className="recovery-step">
                <div className="recovery-step-number">3</div>
                <div className="recovery-step-content">
                  <h3>Delete lockitup.exe</h3>
                  <p>Open File Explorer and delete the lockitup file.</p>
                </div>
              </div>

              <div className="recovery-step">
                <div className="recovery-step-number">4</div>
                <div className="recovery-step-content">
                  <h3>Restart normally</h3>
                </div>
              </div>
            </div>

            <p className="recovery-note">
              Your laptop will work as usual again.
            </p>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="guarantee-section">
        <div className="container">
          <div className="guarantee-box">
            <h2 className="guarantee-title">🛡 Peace of Mind Guarantee</h2>
            
            <div className="guarantee-content">
              <p>lockitup never uploads, stores, or shares your data.</p>
              <p>Everything runs only on your laptop.</p>
              <p className="guarantee-highlight">
                <strong>Your privacy is fully yours.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="container">
          <div className="final-cta">
            <h3>Ready to protect your laptop?</h3>
            <a 
              href="https://github.com/lockitup/lockitup/releases/latest/download/lockitup.exe" 
              className="final-cta-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download lockitup.exe Now
            </a>
            <p className="final-note">Completely free. Fully secure. Forever.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Download;
